"use client";

import { useEffect, useMemo, useRef, useState } from "react";

type CountUpOptions = {
  from?: number;
  to: number;
  durationMs?: number; // total animation duration
  delayMs?: number; // optional delay after entering view
  decimals?: number;
  once?: boolean;
  threshold?: number;
  rootMargin?: string;
  easing?: (t: number) => number; // t: 0..1
};

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

export function useCountUpOnView(options: CountUpOptions) {
  const {
    from = 0,
    to,
    durationMs = 900,
    delayMs = 0,
    decimals = 0,
    once = true,
    threshold = 0.25,
    rootMargin = "0px",
    easing = easeOutCubic,
  } = options;

  const ref = useRef<HTMLElement | null>(null);

  const [hasEntered, setHasEntered] = useState(false);
  const [value, setValue] = useState(from);

  const formatter = useMemo(() => {
    return new Intl.NumberFormat(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }, [decimals]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const reduceMotion =
      window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false;

    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          setHasEntered(true);
          if (once) io.disconnect();
        } else if (!once) {
          setHasEntered(false);
        }
      },
      { threshold, rootMargin },
    );

    io.observe(el);
    return () => io.disconnect();
  }, [once, threshold, rootMargin]);

  useEffect(() => {
    if (!hasEntered) {
      // if replay mode, reset
      if (!once) setValue(from);
      return;
    }

    const reduceMotion =
      window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false;

    if (reduceMotion) {
      setValue(to);
      return;
    }

    let raf = 0;
    let start: number | null = null;
    let timeoutId: number | null = null;

    const startAnim = () => {
      const tick = (ts: number) => {
        if (start === null) start = ts;
        const elapsed = ts - start;
        const t = Math.min(1, elapsed / durationMs);
        const eased = easing(t);
        const next = from + (to - from) * eased;
        setValue(next);

        if (t < 1) raf = requestAnimationFrame(tick);
      };

      raf = requestAnimationFrame(tick);
    };

    if (delayMs > 0) {
      timeoutId = window.setTimeout(startAnim, delayMs);
    } else {
      startAnim();
    }

    return () => {
      if (timeoutId) window.clearTimeout(timeoutId);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [hasEntered, from, to, durationMs, delayMs, easing, once]);

  return { ref, value, format: (n: number) => formatter.format(n) };
}
